/**
 * Running a signing session.
 *
 * The rule the rest of this file exists to keep: nothing is ever written to
 * the audit log after the fact. Every step records itself as it happens, in
 * order, and the certificate is generated from those rows. A log assembled at
 * the end from what the code remembers is a log that says whatever the code
 * wanted it to say.
 */

import { supabase } from "../../lib/supabase";
import { buildRetailPacket, fetchFromPublic } from "../../forms/fillPdf";
import { PDFDocument } from "pdf-lib";
import { loadDealContext, withWarrantyTerms } from "./buildPacket";
import { archiveDealDocument } from "../../lib/documents";
import { sha256Hex, type Marks } from "../../lib/signing/stamp";
import { rolesNeeded, ROLE_LABEL, type SignerRole } from "../../lib/signing/anchors";
import {
  buildSignatureCertificate, ESIGN_DISCLOSURE,
  type CertEvent, type CertSigner,
} from "../../forms/signatureCertificate";
import { fullName } from "../../forms/mappings";
import { FORMS } from "../../forms/mappings";

export type SigningMode = "in_person" | "remote";
export type SigningStatus =
  | "draft" | "sent" | "in_progress" | "completed" | "declined" | "expired" | "voided";

export interface SigningSession {
  id: string;
  deal_id: string;
  mode: SigningMode;
  status: SigningStatus;
  doc_keys: string[];
  expires_at: string | null;
  id_method: string | null;
  id_evidence: Record<string, unknown> | null;
  consent_at: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface StoredSignature {
  role: SignerRole;
  image: string;
  initials: string | null;
  signed_at: string;
  ip: string | null;
}

const SESSION_COLS =
  "id,deal_id,mode,status,doc_keys,expires_at,id_method,id_evidence,consent_at,created_at,completed_at";

/* ------------------------------------------------------------------ */
/* The log                                                             */
/* ------------------------------------------------------------------ */

/**
 * Record what just happened.
 *
 * Deliberately not awaited by every caller: a failure to log must not stop
 * somebody signing, but it must be visible, so it warns rather than
 * disappearing into a swallowed promise.
 */
export async function logEvent(
  sessionId: string,
  kind: string,
  opts: { actor?: string | null; detail?: Record<string, unknown> } = {},
): Promise<void> {
  const { error } = await supabase.from("signing_events").insert({
    session_id: sessionId,
    kind,
    actor: opts.actor ?? null,
    detail: opts.detail ?? null,
    // The browser cannot see its own public address. The worker fills this in
    // for remote signing, where it actually matters; in person the dealer's
    // own network is not evidence of anything.
    user_agent: navigator.userAgent.slice(0, 400),
  });
  if (error) console.warn(`Signing event "${kind}" not recorded: ${error.message}`);
}

/* ------------------------------------------------------------------ */
/* Sessions                                                            */
/* ------------------------------------------------------------------ */

export async function listSessions(dealId: string): Promise<SigningSession[]> {
  const { data, error } = await supabase
    .from("signing_sessions").select(SESSION_COLS)
    .eq("deal_id", dealId).order("created_at", { ascending: false });
  if (error) throw new Error(`Could not load signing history: ${error.message}`);
  return (data ?? []) as SigningSession[];
}

export async function activeSession(dealId: string): Promise<SigningSession | null> {
  const all = await listSessions(dealId);
  return all.find((s) => s.status === "draft" || s.status === "sent"
                       || s.status === "in_progress") ?? null;
}

export async function loadSession(sessionId: string): Promise<SigningSession> {
  const { data, error } = await supabase
    .from("signing_sessions").select(SESSION_COLS).eq("id", sessionId).single();
  if (error || !data) throw new Error("That signing session could not be found.");
  return data as SigningSession;
}

/**
 * A session and what has actually been signed in it.
 *
 * The dealer's screen needs both together. Showing only the session says
 * "waiting" whether the customer has signed or not touched the link, which is
 * the difference between nothing to do and a packet sitting one signature away
 * from finished. On 1 October 2026 a customer signed from her phone and the
 * deal screen went on saying "waiting", so nobody knew.
 */
export interface SessionProgress {
  session: SigningSession;
  signatures: StoredSignature[];
}

export async function pendingProgress(dealId: string): Promise<SessionProgress | null> {
  const session = await activeSession(dealId);
  if (!session) return null;
  return { session, signatures: await loadSignatures(session.id) };
}

/** Everything already signed in a session, keyed by role. */
export async function loadSignatures(sessionId: string): Promise<StoredSignature[]> {
  const { data, error } = await supabase
    .from("signatures").select("role,image,initials,signed_at,ip")
    .eq("session_id", sessionId);
  if (error) throw new Error(`Could not load signatures: ${error.message}`);
  return (data ?? []) as StoredSignature[];
}

export const marksFrom = (sigs: StoredSignature[]): Marks =>
  Object.fromEntries(sigs.map((s) => [s.role, { image: s.image, initials: s.initials }]));

/**
 * Start an in-person session.
 *
 * The identity evidence is captured here rather than at the end, because it
 * describes something that happened before anyone signed: a person looked at
 * a licence. Recording it afterwards would make the certificate a
 * reconstruction.
 */
export async function startInPerson(
  dealId: string,
  docKeys: string[],
  identity: {
    idType: string; idState: string; idLast4: string; idExpiration: string | null;
    attestedByName: string;
  },
): Promise<SigningSession> {
  const { data: user } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from("signing_sessions")
    .insert({
      deal_id: dealId,
      mode: "in_person",
      status: "in_progress",
      doc_keys: docKeys,
      id_method: "in_person_dl",
      id_evidence: {
        id_type: identity.idType,
        id_state: identity.idState,
        id_last4: identity.idLast4,
        id_expiration: identity.idExpiration,
        verified_by: identity.attestedByName,
      },
      attested_by: user?.user?.id ?? null,
      created_by: user?.user?.id ?? null,
    })
    .select(SESSION_COLS)
    .single();

  if (error || !data) {
    throw new Error(`Could not start signing: ${error?.message ?? "no session returned"}`);
  }
  const session = data as SigningSession;

  await logEvent(session.id, "session_created", { actor: identity.attestedByName });
  await logEvent(session.id, "identity_attested", {
    actor: identity.attestedByName,
    detail: {
      method: "in_person_photo_id",
      id_type: identity.idType,
      id_state: identity.idState,
      id_last4: identity.idLast4,
    },
  });
  return session;
}

/** The buyer agreed to sign electronically. Hashed so the wording is pinned. */
export async function recordConsent(sessionId: string, signerName: string): Promise<void> {
  const hash = await sha256Hex(new TextEncoder().encode(ESIGN_DISCLOSURE));
  await logEvent(sessionId, "consent_shown", { actor: signerName });

  const { error } = await supabase
    .from("signing_sessions")
    .update({ consent_at: new Date().toISOString(), consent_sha256: hash })
    .eq("id", sessionId);
  if (error) throw new Error(`Could not record consent: ${error.message}`);

  await logEvent(sessionId, "consent_given", {
    actor: signerName,
    detail: { disclosure_sha256: hash },
  });
}

export async function saveSignature(
  sessionId: string,
  role: SignerRole,
  signerName: string,
  image: string,
  initials: string | null,
): Promise<void> {
  const { error } = await supabase
    .from("signatures")
    .upsert(
      { session_id: sessionId, role, image, initials,
        user_agent: navigator.userAgent.slice(0, 400) },
      { onConflict: "session_id,role" },
    );
  if (error) throw new Error(`Could not save that signature: ${error.message}`);
  await logEvent(sessionId, "signature_adopted", {
    actor: signerName, detail: { role },
  });
}

export async function declineSigning(sessionId: string, who: string, why: string) {
  await logEvent(sessionId, "declined", { actor: who, detail: { reason: why } });
  const { error } = await supabase
    .from("signing_sessions").update({ status: "declined" }).eq("id", sessionId);
  if (error) throw new Error(error.message);
}

/* ------------------------------------------------------------------ */
/* Sealing                                                             */
/* ------------------------------------------------------------------ */

export interface SealedPacket {
  bytes: Uint8Array;
  sha256: string;
  pageCount: number;
}

/**
 * Build the signed packet, append the certificate, hash it, and file it.
 *
 * The hash covers the signed documents WITHOUT the certificate, because the
 * certificate quotes that hash — a document cannot contain its own hash. The
 * certificate is then appended, and the whole thing is what gets archived and
 * emailed.
 */
export async function sealPacket(
  sessionId: string,
  opts: { attestedByName: string } = { attestedByName: "" },
): Promise<SealedPacket> {
  const { data: raw, error } = await supabase
    .from("signing_sessions").select(SESSION_COLS + ",consent_sha256,attested_by")
    .eq("id", sessionId).single();
  if (error || !raw) throw new Error("That signing session could not be found.");
  const session = raw as unknown as SigningSession & { consent_sha256: string | null };

  const ctx = await loadDealContext(session.deal_id);
  const sigs = await loadSignatures(sessionId);
  if (sigs.length === 0) throw new Error("Nothing has been signed yet.");

  const signedOn = new Date().toISOString().slice(0, 10);
  const packet = await buildRetailPacket(
    ctx, fetchFromPublic, session.doc_keys,
    { marks: marksFrom(sigs), signedOn },
  );
  /* The Buyers Guide says "Full terms attached". Until this was here, on the
     signing path they were not. */
  const packetBytes = await withWarrantyTerms(packet.bytes, ctx);

  /* An anchor that did not resolve means a signature the signer thought they
     were giving is not on the paper. That is the one failure here that must
     never pass quietly. */
  const missed = packet.reports.flatMap((r) =>
    (r.stamped?.missing ?? []).map((m) => `${r.formKey}: ${ROLE_LABEL[m.role]} — ${m.why}`));
  if (missed.length) {
    throw new Error(
      `Some signatures could not be placed, so the packet was not sealed:\n${missed.join("\n")}`,
    );
  }

  const packetHash = await sha256Hex(packetBytes);

  // The audit trail, straight from the table.
  const { data: eventRows } = await supabase
    .from("signing_events").select("at,kind,actor,ip,detail")
    .eq("session_id", sessionId).order("at");

  const people: Record<SignerRole, string> = {
    buyer: fullName(ctx.buyer),
    co_buyer: ctx.coBuyer ? fullName(ctx.coBuyer) : "",
    dealer: ctx.dealer.dba ?? ctx.dealer.legalName,
  };

  const certificate = await buildSignatureCertificate({
    dealer: ctx.dealer,
    dealNo: ctx.deal.dealNo,
    vehicle: `${ctx.vehicle.year} ${ctx.vehicle.make} ${ctx.vehicle.model}`,
    vin: ctx.vehicle.vin,
    sessionId,
    mode: session.mode,
    idMethod: session.id_method ?? "unknown",
    idEvidence: session.id_evidence,
    attestedBy: opts.attestedByName
      || (session.id_evidence?.verified_by as string | undefined)
      || null,
    consentAt: session.consent_at,
    consentSha256: session.consent_sha256,
    signers: sigs.map<CertSigner>((s) => ({
      role: s.role,
      name: people[s.role] || ROLE_LABEL[s.role],
      signedAt: s.signed_at,
      ip: s.ip,
      userAgent: null,
    })),
    events: (eventRows ?? []) as CertEvent[],
    documents: session.doc_keys.map((k) => ({
      title: FORMS.find((f) => f.key === k)?.title ?? k,
      pages: 0,
    })),
    packetSha256: packetHash,
  });

  // Append the certificate to the signed documents.
  const merged = await PDFDocument.create();
  for (const part of [packetBytes, certificate]) {
    const src = await PDFDocument.load(part);
    const copied = await merged.copyPages(src, src.getPageIndices());
    copied.forEach((p) => merged.addPage(p));
  }
  merged.setTitle(`Deal ${ctx.deal.dealNo} — signed packet`);
  merged.setProducer("FrontLot");
  const bytes = await merged.save();

  await logEvent(sessionId, "signature_applied", {
    actor: "FrontLot",
    detail: { packet_sha256: packetHash, documents: session.doc_keys },
  });

  const { data: dealRow } = await supabase
    .from("deals").select("status").eq("id", session.deal_id).single();

  const archived = await archiveDealDocument(bytes, {
    dealId: session.deal_id,
    docType: "signed_packet",
    dealStatus: (dealRow?.status as string) ?? "sold",
  }).catch((e) => {
    console.warn("Signed packet not archived:", e);
    return null;
  });

  if (archived) {
    await supabase.from("documents")
      .update({ sha256: packetHash, signing_session_id: sessionId })
      .eq("id", archived.id);
  }

  const { error: doneErr } = await supabase
    .from("signing_sessions")
    .update({ status: "completed", completed_at: new Date().toISOString() })
    .eq("id", sessionId);
  if (doneErr) throw new Error(`Packet built but session not closed: ${doneErr.message}`);

  await logEvent(sessionId, "session_completed", { actor: "FrontLot" });

  return { bytes, sha256: packetHash, pageCount: merged.getPageCount() };
}

/** Who still has to sign before this session can be sealed. */
export function outstanding(
  docKeys: string[], hasCoBuyer: boolean, signed: StoredSignature[],
): SignerRole[] {
  const done = new Set(signed.map((s) => s.role));
  return rolesNeeded(docKeys, hasCoBuyer).filter((r) => !done.has(r));
}

export { ESIGN_DISCLOSURE };

/* ------------------------------------------------------------------ */
/* Remote signing — the dealer's half                                  */
/* ------------------------------------------------------------------ */

import { newToken, newCode, sha256Hex as hashText } from "../../lib/signing/remote";
import { emailDocument } from "../../lib/stripe";
import { BUCKET } from "../../lib/documents";

export interface SentLink {
  sessionId: string;
  /** Read this to the customer over the phone. Shown once and never stored. */
  code: string;
  url: string;
  expiresAt: string;
}

/**
 * Send a signing link.
 *
 * Two secrets on two channels: the link goes by email, the code is spoken. A
 * code emailed alongside its own link is not a second factor, it is a longer
 * link — so the dealer reads it out and, in doing so, speaks to the person
 * they think they are dealing with.
 *
 * Neither secret is stored. Only their hashes go to the database, so a copy of
 * it is not a set of working links.
 */
export async function sendSigningLink(
  dealId: string,
  docKeys: string[],
  to: { email: string; name: string; vehicle: string; dealNo: string; dealerName: string },
  opts: { expiresInHours?: number } = {},
): Promise<SentLink> {
  const token = newToken();
  const code = newCode();
  const hours = opts.expiresInHours ?? 72;
  const expiresAt = new Date(Date.now() + hours * 3600_000).toISOString();

  const { data: user } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from("signing_sessions")
    .insert({
      deal_id: dealId,
      mode: "remote",
      status: "sent",
      doc_keys: docKeys,
      token_hash: await hashText(token),
      code_hash: await hashText(code),
      expires_at: expiresAt,
      id_method: "sms_code",
      id_evidence: { channel: "code read by telephone", email: to.email },
      created_by: user?.user?.id ?? null,
    })
    .select(SESSION_COLS)
    .single();

  if (error || !data) {
    throw new Error(`Could not create the signing link: ${error?.message ?? "no session"}`);
  }
  const session = data as SigningSession;
  await logEvent(session.id, "session_created", { detail: { mode: "remote" } });

  /* Upload the packet the customer will read. Built unsigned, because that is
     what they are being asked to look at. */
  const ctx = await loadDealContext(dealId);
  const built = await buildRetailPacket(ctx, fetchFromPublic, docKeys);
  /* With the terms on the end, so the customer reads what they are agreeing
     to rather than a guide that refers to pages they were never shown. */
  const packetBytes = await withWarrantyTerms(built.bytes, ctx);
  const buffer = new ArrayBuffer(packetBytes.byteLength);
  new Uint8Array(buffer).set(packetBytes);

  const up = await supabase.storage
    .from(BUCKET)
    .upload(`signing/${session.id}/packet.pdf`,
            new Blob([buffer], { type: "application/pdf" }),
            { contentType: "application/pdf", upsert: false });

  if (up.error) {
    await supabase.from("signing_sessions").update({ status: "voided" }).eq("id", session.id);
    throw new Error(`Could not stage the packet for signing: ${up.error.message}`);
  }

  const url = `${window.location.origin}/?sign=${token}`;

  await emailDocument({
    to: to.email,
    subject: `Sign your paperwork — ${to.vehicle}`,
    message:
      `Hi ${to.name.split(" ")[0] ?? to.name},\n\n`
      + `Your paperwork for the ${to.vehicle} is ready to sign.\n\n`
      + `${url}\n\n`
      + `You'll need the six-digit code we'll read you over the phone. `
      + `The link works until ${new Date(expiresAt).toLocaleString()}.\n\n`
      + `Please don't forward this link to anyone.\n\n`
      + `Thanks,\n${to.dealerName}`,
    // No attachment: the packet is behind the code, not in the inbox.
    filename: "signing-link.pdf",
    pdfBase64: "",
    dealId,
  }).catch(async (e: Error) => {
    await logEvent(session.id, "email_failed", { detail: { error: e.message } });
    throw new Error(
      `The link was created but the email did not send: ${e.message}. `
      + `You can still read the customer the link and code.`,
    );
  });

  await logEvent(session.id, "link_sent", { detail: { to: to.email, expires_at: expiresAt } });

  return { sessionId: session.id, code, url, expiresAt };
}
